import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, Plus, X, Upload, Pencil, Calendar, MapPin, Image as ImageIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const emptyForm = {
  id: null,
  title: "",
  date_start: "",
  date_end: "",
  duration: "",
  location: "",
  activities: "",
  description: "",
  cover_image: "",
  gallery_images: [],
  is_published: true,
};

// Read a file and compress to a JPEG data URL, max dimension 1600px.
function readAndCompress(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Invalid image"));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function fmtDateRange(start, end) {
  const opts = { day: "numeric", month: "short", year: "numeric" };
  try {
    const s = new Date(start).toLocaleDateString(undefined, opts);
    if (!end || end === start) return s;
    const e = new Date(end).toLocaleDateString(undefined, opts);
    return `${s} — ${e}`;
  } catch {
    return start;
  }
}

export function EventsPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/events");
      setEvents(data);
    } catch {
      toast.error("Could not load events");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resetForm = () => { setForm(emptyForm); setFormOpen(false); };

  const startEdit = (ev) => {
    setForm({
      id: ev.id,
      title: ev.title || "",
      date_start: ev.date_start || "",
      date_end: ev.date_end || "",
      duration: ev.duration || "",
      location: ev.location || "",
      activities: (ev.activities || []).join(", "),
      description: ev.description || "",
      cover_image: ev.cover_image || "",
      gallery_images: ev.gallery_images || [],
      is_published: ev.is_published !== false,
    });
    setFormOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onCoverPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await readAndCompress(file);
      onField("cover_image", dataUrl);
    } catch (err) {
      toast.error(err.message || "Image failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onGalleryPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map((f) => readAndCompress(f)));
      setForm((f) => ({ ...f, gallery_images: [...(f.gallery_images || []), ...urls] }));
    } catch (err) {
      toast.error(err.message || "Image failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeGalleryAt = (i) =>
    setForm((f) => ({ ...f, gallery_images: f.gallery_images.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date_start || !form.location.trim()) {
      toast.error("Title, start date and location are required");
      return;
    }
    const payload = {
      title: form.title.trim(),
      date_start: form.date_start,
      date_end: form.date_end || null,
      duration: form.duration.trim() || null,
      location: form.location.trim(),
      activities: form.activities
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      description: form.description.trim() || null,
      cover_image: form.cover_image || null,
      gallery_images: form.gallery_images || [],
      is_published: !!form.is_published,
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/events/${form.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Event added");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this event?")) return;
    try {
      await api.delete(`/admin/events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="font-body text-xs uppercase tracking-[0.16em] text-[#8C8070]">Events</p>
          <p className="font-display text-3xl mt-1">{events.length} total</p>
        </div>
        {!formOpen && (
          <button
            data-testid="events-new"
            onClick={() => { setForm(emptyForm); setFormOpen(true); }}
            className="font-body text-sm uppercase tracking-[0.12em] font-medium bg-[#1A1A1A] text-[#FAF7F2] px-5 py-2.5 rounded-full inline-flex items-center gap-2 hover:scale-[1.03] transition-transform"
          >
            <Plus className="h-4 w-4" /> Add event
          </button>
        )}
      </div>

      {/* Form */}
      {formOpen && (
        <form
          onSubmit={save}
          data-testid="events-form"
          className="rounded-sm border border-[#8C8070]/25 p-5 md:p-7 mb-10 bg-white/60"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-2xl">{form.id ? "Edit event" : "New event"}</h3>
            <button type="button" onClick={resetForm} className="text-[#8C8070] hover:text-[#1A1A1A]" aria-label="Close form">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Title" required>
              <input
                data-testid="event-title"
                value={form.title}
                onChange={(e) => onField("title", e.target.value)}
                placeholder="Silent Mornings — Coorg Retreat"
                className={inputCls}
              />
            </Field>
            <Field label="Location" required>
              <input
                data-testid="event-location"
                value={form.location}
                onChange={(e) => onField("location", e.target.value)}
                placeholder="Coorg, Karnataka"
                className={inputCls}
              />
            </Field>
            <Field label="Start date" required>
              <input
                data-testid="event-date-start"
                type="date"
                value={form.date_start}
                onChange={(e) => onField("date_start", e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="End date">
              <input
                data-testid="event-date-end"
                type="date"
                value={form.date_end}
                onChange={(e) => onField("date_end", e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Duration">
              <input
                data-testid="event-duration"
                value={form.duration}
                onChange={(e) => onField("duration", e.target.value)}
                placeholder="3 days / 2 nights"
                className={inputCls}
              />
            </Field>
            <Field label="Activities (comma-separated)">
              <input
                data-testid="event-activities"
                value={form.activities}
                onChange={(e) => onField("activities", e.target.value)}
                placeholder="Yoga, Breathwork, Forest walks, Silent meals"
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Description" className="mt-5">
            <textarea
              data-testid="event-description"
              rows={4}
              value={form.description}
              onChange={(e) => onField("description", e.target.value)}
              placeholder="A gentle, immersive weekend of unhurried practice…"
              className={`${inputCls} resize-none`}
            />
          </Field>

          {/* Cover image */}
          <div className="mt-6">
            <p className="font-body text-xs uppercase tracking-[0.16em] text-[#8C8070] mb-2">Cover image</p>
            <div className="flex items-start gap-4 flex-wrap">
              {form.cover_image ? (
                <div className="relative w-40 h-28 rounded-sm overflow-hidden border border-[#8C8070]/30">
                  <img src={form.cover_image} alt="Cover" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => onField("cover_image", "")}
                    className="absolute top-1 right-1 bg-[#1A1A1A]/80 text-white rounded-full p-1"
                    aria-label="Remove cover"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div className="w-40 h-28 rounded-sm border border-dashed border-[#8C8070]/50 flex items-center justify-center text-[#8C8070]">
                  <ImageIcon className="h-5 w-5" />
                </div>
              )}
              <label className="inline-flex items-center gap-2 font-body text-sm px-4 py-2 rounded-full border border-[#1A1A1A]/25 hover:bg-[#1A1A1A] hover:text-[#FAF7F2] cursor-pointer transition-colors">
                <Upload className="h-4 w-4" />
                {form.cover_image ? "Replace" : "Upload cover"}
                <input data-testid="event-cover-input" type="file" accept="image/*" className="hidden" onChange={onCoverPick} />
              </label>
            </div>
          </div>

          {/* Gallery */}
          <div className="mt-6">
            <p className="font-body text-xs uppercase tracking-[0.16em] text-[#8C8070] mb-2">Gallery</p>
            <div className="flex flex-wrap gap-3 mb-3">
              {(form.gallery_images || []).map((src, i) => (
                <div key={i} className="relative w-24 h-24 rounded-sm overflow-hidden border border-[#8C8070]/30">
                  <img src={src} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeGalleryAt(i)}
                    className="absolute top-1 right-1 bg-[#1A1A1A]/80 text-white rounded-full p-1"
                    aria-label="Remove image"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 font-body text-sm px-4 py-2 rounded-full border border-[#1A1A1A]/25 hover:bg-[#1A1A1A] hover:text-[#FAF7F2] cursor-pointer transition-colors">
              <Upload className="h-4 w-4" />
              Add photos
              <input data-testid="event-gallery-input" type="file" accept="image/*" multiple className="hidden" onChange={onGalleryPick} />
            </label>
          </div>

          {/* Publish + actions */}
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
            <label className="inline-flex items-center gap-3 font-body text-sm cursor-pointer">
              <input
                data-testid="event-published"
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => onField("is_published", e.target.checked)}
                className="h-4 w-4 accent-[#3B5A3A]"
              />
              Publish on the site
            </label>
            <div className="flex gap-3">
              <button type="button" onClick={resetForm} className="font-body text-sm px-5 py-2.5 rounded-full border border-[#1A1A1A]/25 hover:bg-[#F2EDE5] transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                data-testid="event-save"
                disabled={saving || uploading}
                className="font-body text-sm uppercase tracking-[0.12em] font-medium bg-[#F5C800] text-[#1A1A1A] px-6 py-2.5 rounded-full hover:scale-[1.02] transition-transform disabled:opacity-60"
              >
                {saving ? "Saving…" : form.id ? "Update event" : "Add event"}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* List */}
      {loading ? (
        <p className="font-body text-[#8C8070] py-16">Loading…</p>
      ) : events.length === 0 ? (
        <p data-testid="events-empty" className="font-body text-[#8C8070] py-16">
          No events yet. Add one to have it appear on the site.
        </p>
      ) : (
        <div className="space-y-4">
          {events.map((ev) => (
            <div
              key={ev.id}
              data-testid={`event-row-${ev.id}`}
              className="rounded-sm border border-[#8C8070]/25 p-4 md:p-5 flex flex-col sm:flex-row gap-4 bg-[#FAF7F2]"
            >
              <div className="w-full sm:w-40 h-32 sm:h-28 rounded-sm overflow-hidden bg-[#F2EDE5] shrink-0">
                {ev.cover_image ? (
                  <img src={ev.cover_image} alt={ev.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#8C8070]">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3 justify-between">
                  <div className="min-w-0">
                    <p className="font-display text-xl md:text-2xl leading-tight truncate">{ev.title}</p>
                    <p className="mt-2 font-body text-sm text-[#1A1A1A]/70 inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" /> {fmtDateRange(ev.date_start, ev.date_end)}
                      {ev.duration ? <span className="text-[#8C8070]"> · {ev.duration}</span> : null}
                    </p>
                    <p className="mt-1 font-body text-sm text-[#1A1A1A]/70 inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" /> {ev.location}
                    </p>
                    {!ev.is_published && (
                      <span className="mt-2 inline-block font-body text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-[#F2EDE5] text-[#8C8070]">
                        Draft
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      data-testid={`event-edit-${ev.id}`}
                      onClick={() => startEdit(ev)}
                      className="text-[#8C8070] hover:text-[#3B5A3A] transition-colors p-2"
                      aria-label={`Edit ${ev.title}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={`event-delete-${ev.id}`}
                      onClick={() => remove(ev.id)}
                      className="text-[#8C8070] hover:text-[#b3261e] transition-colors p-2"
                      aria-label={`Delete ${ev.title}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const inputCls =
  "mt-1.5 w-full bg-transparent border-b-2 border-[#1A1A1A]/25 py-2 font-body text-base placeholder-[#1A1A1A]/35 focus:outline-none focus:border-[#3B5A3A] transition-colors duration-300";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-body text-xs uppercase tracking-[0.16em] text-[#8C8070]">
        {label} {required && <span className="text-[#b3261e]">*</span>}
      </span>
      {children}
    </label>
  );
}
